import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, ChevronDown, LifeBuoy } from "lucide-react";
import Reveal from "@/components/Reveal";

const EMAIL = "knotforgottenapp@gmail.com";

const FAQS = [
  {
    q: "When will Knot Forgotten be available on the App Store?",
    a: "Knot Forgotten is coming soon to the iOS App Store. We're in final preparation for launch. Questions in the meantime? Email us and we'll be happy to help.",
  },
  {
    q: "How do I add a boat?",
    a: "The first time you open the app, a guided onboarding walks you through adding your boat step by step — details, engines, systems, and storage. After that, you can add more boats from the boat switcher. The free tier includes one boat; Knot Forgotten Plus supports multiple boats with a fleet dashboard.",
  },
  {
    q: "How do I add a maintenance record?",
    a: "Open the Maintenance tab and tap to log maintenance or a repair. If the task came from a Smart Maintenance suggestion, tapping its card opens the form with the rule, engine, and next-due date already filled in — you just confirm the date, hours, vendor, and cost.",
  },
  {
    q: "How do reminders work?",
    a: "Knot Forgotten schedules on-device (local) notifications 14 days and 7 days before upcoming maintenance is due and before documents, warranties, or registrations expire. Reminders fire at 9am local time and work entirely on your phone — no push server involved. You can turn them on or off anytime in Profile → Reminders.",
  },
  {
    q: "How do I manage or cancel my subscription?",
    a: "Subscriptions purchased in the iOS app are managed by Apple. On your iPhone, open Settings → your name → Subscriptions, select Knot Forgotten, and make your changes there. The app's subscription screen also includes a Restore Purchases option if you reinstall or switch devices.",
  },
  {
    q: "Is there a free trial?",
    a: "Yes. Every new account includes a 30-day Knot Forgotten Plus trial — no payment method required. When the trial ends, your account simply returns to the free tier unless you choose to subscribe.",
  },
  {
    q: "How do I delete my account?",
    a: "Inside the app, go to Profile → Danger Zone → Delete my account. This permanently deletes your account, boats, records, documents, and photos. This action is irreversible. You can also email us and we'll take care of it.",
  },
];

function FaqItem({ item, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="overflow-hidden rounded-2xl border border-slate-200 bg-white transition-colors hover:border-[#1D8A99]/40"
      data-testid={`faq-accordion-item-${index}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`faq-question-${index}`}
        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
      >
        <span className="text-[15px] font-semibold text-[#0A192F] sm:text-base">{item.q}</span>
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.25 }} className="shrink-0 text-[#1D8A99]">
          <ChevronDown className="h-5 w-5" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="px-6 pb-6 text-sm leading-relaxed text-slate-600" data-testid={`faq-answer-${index}`}>
              {item.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Support() {
  useEffect(() => {
    document.title = "Support | Knot Forgotten";
  }, []);

  return (
    <div className="bg-[#F8FAFC] pt-[72px]" data-testid="support-page">
      <div className="border-b border-slate-200 bg-[#0A192F]">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
          <p className="font-mono-label text-[11px] uppercase tracking-[0.3em] text-[#25A5B7]">Support</p>
          <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl" data-testid="support-title">
            Knot Forgotten Support
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            Need help with Knot Forgotten? We usually reply within 1–2 business days.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal>
            <div className="rounded-[24px] border border-[#1D8A99]/25 bg-[#0A192F] p-8 sm:p-10" data-testid="support-email-card">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#1D8A99]/15 text-[#25A5B7]">
                <LifeBuoy className="h-6 w-6" />
              </span>
              <h2 className="mt-6 text-xl font-bold tracking-tight text-white sm:text-2xl">
                Talk to a human
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-300 sm:text-base">
                Bug reports, feature ideas, billing questions — send it all our way.
              </p>
              <a
                href={`mailto:${EMAIL}?subject=${encodeURIComponent("Knot Forgotten support")}`}
                data-testid="support-mailto-link"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#1D8A99] px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#25A5B7] hover:shadow-[0_10px_40px_-10px_rgba(29,138,153,0.6)]"
              >
                <Mail className="h-4 w-4" />
                {EMAIL}
              </a>
              <p className="mt-5 text-xs text-slate-400">
                Opens your mail app. Response time: 1–2 business days.
              </p>
            </div>
          </Reveal>

          <div>
            <Reveal>
              <p className="font-mono-label text-[11px] uppercase tracking-[0.3em] text-[#1D8A99]">
                Frequently asked
              </p>
            </Reveal>
            <div className="mt-6 flex flex-col gap-4" data-testid="faq-list">
              {FAQS.map((item, i) => (
                <Reveal key={item.q} delay={i * 0.05}>
                  <FaqItem item={item} index={i} />
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
